"use client";

import {
    FC,
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { IBonusResponse, IReferralPersonalReport } from "@finbackoffice/clientbff-client";
import { BonusType, BonusVertical } from "@finbackoffice/enums";
import { ClientBFFContext } from "./client.provider";

type IReferralReportContext = {
    handleFilterChange: (updatedFilter: IReferralReportFilter) => void;
    loadReferralTimeframes: (payload: {
        type: BonusType;
        scope: BonusVertical;
        count: number;
    }) => Promise<void>;
    loadReferralReport: (payload: { bonusId: number }) => Promise<void>;
    findReferral: () => Promise<void>;
    createReferralLink: (slug: string) => string;
    filter: IReferralReportFilter;
    referralLink: string | null;
    referralUserSlugState: IReferralUserSlugState;
    referralReportReady: boolean;
    referralReport: IReferralPersonalReport | null;
    referralTimeframesReady: boolean;
    referralTimeframes: IBonusResponse[];
};

export const ReferralReportContext = createContext<IReferralReportContext>(null as any);

type ReferralsState = {
    loading: boolean;
    data: IReferralPersonalReport | null;
};

type TimeframesState = {
    loading: boolean;
    data: IBonusResponse[];
};

type IReferralUserSlugState = {
    slug: string | null;
    loading: boolean;
};

const REFERRAL_PARAM_NAME = "ref";

export interface IReferralReportFilter {
    turnover: "" | "active";
    period: string;
}

type ReferralReportProviderProps = PropsWithChildren<{ domain?: string }>;

export const ReferralReportProvider: FC<ReferralReportProviderProps> = ({ children, domain }) => {
    const client = useContext(ClientBFFContext);
    const [filter, setFilter] = useState<IReferralReportFilter>({
        turnover: "active",
        period: "",
    });
    const [referrals, setReferrals] = useState<ReferralsState>({ data: null, loading: false });
    const [timeframes, setTimeframes] = useState<TimeframesState>({
        loading: false,
        data: [],
    });

    useEffect(() => {
        if (filter.period) {
            loadReferralReport({
                bonusId: parseInt(filter.period),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.period]);

    useEffect(() => {
        findReferral();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = useCallback((updatedFilter: IReferralReportFilter) => {
        setFilter(updatedFilter);
    }, []);

    const [referralLink, setReferralLink] = useState<string | null>(null);
    const [referralUserSlugState, setReferralUserSlugState] = useState<IReferralUserSlugState>({
        slug: "",
        loading: false,
    });

    const createReferralLink = useCallback(
        (slug: string) => {
            return `https://${domain}/?${REFERRAL_PARAM_NAME}=${encodeURIComponent(slug)}`;
        },
        [domain],
    );

    const findReferral = useCallback(async () => {
        setReferralUserSlugState((state) => ({
            ...state,
            loading: true,
        }));
        try {
            const response = await client.findReferral();
            if (response?.slug) {
                setReferralLink(createReferralLink(response.slug));
                setReferralUserSlugState((state) => ({
                    ...state,
                    slug: response.slug,
                    loading: false,
                }));
            } else {
                setReferralUserSlugState((state) => ({
                    ...state,
                    slug: null,
                    loading: false,
                }));
            }
        } catch (_error: unknown) {
            setReferralUserSlugState((state) => ({
                ...state,
                slug: null,
                loading: false,
            }));
        }
    }, [client, createReferralLink]);

    const loadReferralTimeframes = useCallback(
        async (payload: { type: BonusType; scope: BonusVertical; count: number }) => {
            const { type, scope, count } = payload;

            setTimeframes((state) => ({
                ...state,
                loading: true,
                data: [],
            }));

            try {
                const response = await client.getBonuses(type, count, scope);
                setTimeframes((state) => ({
                    ...state,
                    loading: false,
                    data: response.items,
                }));
            } catch (_error: unknown) {
                setTimeframes((state) => ({
                    ...state,
                    loading: false,
                    data: [],
                }));
            }
        },
        [client],
    );

    const loadReferralReport = useCallback(
        async (payload: { bonusId: number }) => {
            const { bonusId } = payload;
            setReferrals((state) => ({
                ...state,
                loading: true,
                data: null,
            }));

            try {
                const response = await client.getReferralPersonalReport(bonusId);
                setReferrals((state) => ({
                    ...state,
                    loading: false,
                    data: response ?? null,
                }));
            } catch (_error: unknown) {
                setReferrals((state) => ({
                    ...state,
                    loading: false,
                    data: null,
                }));
            }
        },
        [client],
    );

    const value = useMemo(
        () => ({
            findReferral,
            loadReferralReport,
            loadReferralTimeframes,
            handleFilterChange,
            createReferralLink,
            filter,
            referralLink,
            referralUserSlugState,
            referralReportReady: !referrals.loading,
            referralReport: referrals.data,
            referralTimeframesReady: !timeframes.loading,
            referralTimeframes: timeframes.data,
        }),
        [
            findReferral,
            loadReferralReport,
            loadReferralTimeframes,
            handleFilterChange,
            createReferralLink,
            filter,
            referrals,
            timeframes,
            referralLink,
            referralUserSlugState,
        ],
    );

    return (
        <ReferralReportContext.Provider value={value}>{children}</ReferralReportContext.Provider>
    );
};
